
import React from 'react';
import { Calendar } from '@/components/ui/calendar';
import { format, isSameDay, isWithinInterval, addDays } from 'date-fns';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight } from 'lucide-react';

export type CalendarModifiers = {
  appointments?: Date[] | undefined;
  unavailable?: Date[] | undefined;
  available?: Date[] | undefined;
  today?: Date | undefined;
  selected?: Date[] | Date | undefined;
  weekHighlight?: Date | undefined;
};

interface CalendarWrapperProps {
  selectedDate: Date | undefined;
  onDateSelect: (date: Date | undefined) => void;
  calendarMonth: Date;
  onMonthChange: (date: Date) => void;
  modifiers?: CalendarModifiers;
  modifiersClassNames?: Record<string, string>;
  disabledDates?: Date[];
  onDayDoubleClick?: (date: Date) => void;
  title?: string;
  className?: string;
  view?: 'month' | 'week' | 'day';
  showControls?: boolean;
  showHeader?: boolean;
  compact?: boolean;
}

const CalendarWrapper: React.FC<CalendarWrapperProps> = ({
  selectedDate,
  onDateSelect,
  calendarMonth,
  onMonthChange,
  modifiers = {},
  modifiersClassNames = {},
  disabledDates = [],
  onDayDoubleClick,
  title,
  className = '',
  view = 'day',
  showControls = true,
  showHeader = true,
  compact = false
}) => {
  const currentMonth = format(calendarMonth, 'MMMM yyyy');
  
  return (
    <div className={`rounded-md border shadow-sm ${className} ${compact ? 'text-sm' : ''}`}>
      {showHeader && title && (
        <div className="flex justify-between items-center p-3 border-b">
          <h3 className={`${compact ? 'text-sm' : 'text-sm font-medium'}`}>{title}</h3>
          {showControls && (
            <div className="flex gap-1">
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={() => onMonthChange(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() - 1, 1))}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={() => onMonthChange(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1, 1))}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
      )}
      
      {showHeader && !title && (
        <div className="flex justify-between items-center p-3 border-b">
          <h3 className={`${compact ? 'text-sm' : 'text-sm font-medium'}`}>{currentMonth}</h3>
          {showControls && (
            <div className="flex gap-1">
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={() => onMonthChange(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() - 1, 1))}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={() => onMonthChange(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1, 1))}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
      )}
      
      <Calendar
        mode="single"
        selected={selectedDate}
        onSelect={onDateSelect}
        month={calendarMonth}
        onMonthChange={onMonthChange}
        className={`w-full ${view === 'week' ? 'week-view' : ''} ${compact ? 'compact-calendar' : ''}`}
        modifiers={modifiers}
        modifiersClassNames={{
          ...modifiersClassNames,
          selected: 'bg-red-500 text-white hover:bg-red-600 rounded-full',
          today: 'border border-cs-primary text-cs-primary font-medium'
        }}
        disabled={disabledDates}
        onDayDoubleClick={onDayDoubleClick}
      />
    </div>
  );
};

export default CalendarWrapper;
